import {useEffect, useRef, useState} from 'react'
import {FormikErrors, FormikValues} from 'formik'
import {UsersSymbolGroup} from '../../projects-list/UsersSymbolGroup'
import {IconUserModel} from '../UsersSymbolGroupColumn'
import {UserAvatar} from '../../../../../../_metronic/helpers'
import {SequenceResponsiblesSymbolGroup} from './components/SequenceResponsiblesSymbolGroup'
import dayjs from 'dayjs'
import {OverlayTrigger, Tooltip, Alert} from 'react-bootstrap'
import {Toast} from '../../../../../../_metronic/partials/widgets'
import {
  formatTaskDateTimeDisplay,
  parseTaskDateTime,
} from '../../../../../../_metronic/helpers/TaskDateTime'

type TaskSequenceResponsiblesProps = {
  isCreate?: boolean
  isClone?: boolean
  users: Array<IconUserModel>
  responsibles?: Array<IconUserModel>
  formikSetFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<FormikErrors<FormikValues>> | Promise<void>
  onAutoSave?: (overrideValues?: {sequence_responsibles?: any[]}) => void
  tabId?: string
}

export function TaskSequenceResponsibles({
  isCreate = false,
  users,
  responsibles = [],
  formikSetFieldValue,
  isClone = false,
  onAutoSave,
  tabId,
}: TaskSequenceResponsiblesProps) {
  const normalizeResponsible = (responsible: IconUserModel & {task_expected_date?: string | null}) => {
    const {task_expected_date, ...rest} = responsible as typeof responsible & {
      task_expected_date?: string | null
    }
    return {
      ...rest,
      expected_date: responsible?.expected_date ?? task_expected_date ?? null,
    }
  }

  const [data, setData] = useState((responsibles ?? []).map(normalizeResponsible))
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [activeDateIndex, setActiveDateIndex] = useState<number | null>(null)
  const dateInputsRef = useRef<Record<number, HTMLInputElement | null>>({})
  const originalDateValuesRef = useRef<Record<number, string | null>>({})
  const isSelectingDateRef = useRef<boolean>(false)

  useEffect(() => {
    setData((responsibles ?? []).map(normalizeResponsible))
    setActiveDateIndex(null)
  }, [responsibles])

  function removeResponsible(index: number) {
    //@ts-ignore
    const newUsers = data?.filter((user, i) => i != index)
    setData(newUsers)
    formikSetFieldValue('sequence_responsibles', newUsers)
    formikSetFieldValue('users', newUsers)
    if (onAutoSave) {
      onAutoSave({sequence_responsibles: newUsers})
    }
    // Ajusta referências de inputs de data
    delete dateInputsRef.current[index]
    Object.keys(dateInputsRef.current).forEach((key) => {
      const keyIndex = Number(key)
      if (keyIndex > index) {
        dateInputsRef.current[keyIndex - 1] = dateInputsRef.current[keyIndex]
        delete dateInputsRef.current[keyIndex]
      }
    })
    if (activeDateIndex !== null) {
      if (activeDateIndex === index) {
        setActiveDateIndex(null)
      } else if (activeDateIndex > index) {
        setActiveDateIndex((prev) => (prev !== null ? prev - 1 : prev))
      }
    }
  }

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', index.toString())
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index)
    }
  }

  const handleDragOverWrapper = (index: number) => (e: React.DragEvent) => {
    handleDragOver(e, index)
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()

    try {
      const draggedIndexStr = e.dataTransfer.getData('text/plain')
      const draggedIndex = parseInt(draggedIndexStr)

      if (isNaN(draggedIndex) || draggedIndex === dropIndex || draggedIndex < 0 || dropIndex < 0) {
        setDraggedIndex(null)
        setDragOverIndex(null)
        return
      }

      // Verificar se os índices são válidos
      if (draggedIndex >= responsibles?.length || dropIndex >= responsibles?.length) {
        setDraggedIndex(null)
        setDragOverIndex(null)
        return
      }

      // Criar nova array sem modificar a original
      const items = [...data]
      const draggedItem = items[draggedIndex]

      // Remover o item da posição original
      items.splice(draggedIndex, 1)

      // Inserir na nova posição
      items.splice(dropIndex, 0, draggedItem)

      // Verifica duplicatas consecutivas
      const hasConsecutiveDuplicates = items.some((item, i) => {
        const prev = items[i - 1]
        // @ts-ignore
        const prevId = prev?.user_id ?? prev?.id ?? null
        // @ts-ignore
        const userId = item.user_id ?? item.id

        return prev && prevId === userId
      })

      if (hasConsecutiveDuplicates) {
        setDraggedIndex(null)
        setDragOverIndex(null)
        return
      }

      // Validar e limpar datas inválidas após reordenamento
      const validatedItems = items.map((item, index) => {
        if (!item.expected_date) {
          return item
        }

        const itemDate = toComparableDate(item.expected_date)
        const maxDate = getMaxPreviousDate(items, index)
        if (maxDate && itemDate && itemDate.isBefore(maxDate)) {
          const {task_expected_date, ...rest} = item as typeof item & {
            task_expected_date?: string | null
          }
          return {...rest, expected_date: null}
        }

        return item
      })

      formikSetFieldValue('sequence_responsibles', validatedItems)
      setData(validatedItems)
      setDraggedIndex(null)
      setDragOverIndex(null)
      if (onAutoSave) {
        onAutoSave({sequence_responsibles: validatedItems})
      }
    } catch (error) {
      console.error('Error in handleDrop:', error)
      setDraggedIndex(null)
      setDragOverIndex(null)
    }
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const toComparableDate = (value?: string | null): dayjs.Dayjs | null => {
    const parsed = parseTaskDateTime(value)
    if (!parsed.date) {
      return null
    }

    return dayjs(parsed.date).endOf('day')
  }

  const getMaxPreviousDate = (
    sequence: Array<IconUserModel & {expected_date?: string | null}>,
    index: number
  ): dayjs.Dayjs | null => {
    if (index === 0) {
      return null
    }

    let maxDate: dayjs.Dayjs | null = null

    for (let i = 0; i < index; i++) {
      const user = sequence[i]
      const userDate = toComparableDate(user?.expected_date)
      if (userDate && (!maxDate || userDate.isAfter(maxDate))) {
        maxDate = userDate
      }
    }

    return maxDate
  }

  const validateDateOrder = (
    sequence: Array<IconUserModel & {expected_date?: string | null}>,
    index: number,
    newDate: string | null
  ): {valid: boolean; errorMessage?: string} => {
    if (!newDate) {
      return {valid: true}
    }

    const selectedDate = toComparableDate(newDate)
    if (!selectedDate) {
      return {valid: true}
    }

    const nextUser = sequence[index + 1]
    if (nextUser?.expected_date) {
      const nextUserDate = toComparableDate(nextUser.expected_date)
      if (nextUserDate && selectedDate.isAfter(nextUserDate)) {
        return {
          valid: false,
          errorMessage: `A data não pode ser posterior à data do próximo responsável (${formatTaskDateTimeDisplay(nextUser.expected_date)})`,
        }
      }
    }

    const maxPreviousDate = getMaxPreviousDate(sequence, index)
    if (maxPreviousDate && selectedDate.isBefore(maxPreviousDate)) {
      return {
        valid: false,
        errorMessage: `A data não pode ser anterior à data de nenhum responsável anterior. A data mínima permitida é ${formatTaskDateTimeDisplay(maxPreviousDate.format('YYYY-MM-DD'))}`,
      }
    }

    return {valid: true}
  }

  const toggleDatePicker = (index: number) => {
    const newIndex = activeDateIndex === index ? null : index
    setActiveDateIndex(newIndex)
    
    if (newIndex !== null) {
      originalDateValuesRef.current[newIndex] = data[newIndex]?.expected_date || null
      isSelectingDateRef.current = false
    }
  }

  const handleDateChange = (index: number, value: string, isUserSelection: boolean = false) => {
    const originalValue = originalDateValuesRef.current[index]
    
    if (isUserSelection) {
      // Se value está vazio, permitir limpar a data
      if (!value || value === '') {
        const normalizedValue = null
        const updated = data.map((user, i) => {
          if (i !== index) {
            return user
          }

          const {task_expected_date, ...rest} = user as typeof user & {
            task_expected_date?: string | null
          }
          return {...rest, expected_date: normalizedValue}
        })

        setData(updated)
        formikSetFieldValue('sequence_responsibles', updated)
        formikSetFieldValue('users', updated)
        if (onAutoSave) {
          onAutoSave({sequence_responsibles: updated})
        }
        
        delete originalDateValuesRef.current[index]
        isSelectingDateRef.current = false
        return
      }

      // Se value existe, validar antes de processar
      if (value) {
        const selectedDate = toComparableDate(value)
        const today = dayjs().startOf('day')
        const maxPreviousDate = getMaxPreviousDate(data, index)
        let minDate = today

        if (maxPreviousDate && maxPreviousDate.isAfter(today)) {
          minDate = maxPreviousDate.startOf('day')
        }

        if (selectedDate && selectedDate.isBefore(minDate)) {
          const input = dateInputsRef.current[index]
          if (input && originalValue) {
            input.value = parseTaskDateTime(originalValue).date || ''
          }

          let errorMessage = 'A data não pode ser anterior ao dia atual'
          if (maxPreviousDate && maxPreviousDate.isAfter(today)) {
            errorMessage = `A data não pode ser anterior à data de nenhum responsável anterior. A data mínima permitida é ${formatTaskDateTimeDisplay(maxPreviousDate.format('YYYY-MM-DD'))}`
          }
          
          Toast.fire({
            icon: 'error',
            title: errorMessage,
            customClass: {
              popup: 'bg-light-danger',
              title: 'text-danger',
            },
          })
          return
        }

        const validation = validateDateOrder(data, index, value)
        if (!validation.valid) {
          const input = dateInputsRef.current[index]
          if (input && originalValue) {
            input.value = originalValue
          }
          Toast.fire({
            icon: 'error',
            title: validation.errorMessage || 'Data inválida para a sequência',
            customClass: {
              popup: 'bg-light-danger',
              title: 'text-danger',
            },
          })
          return
        }
      }
      
      // Processar a atualização
      const normalizedValue = value || null
      const updated = data.map((user, i) => {
        if (i !== index) {
          return user
        }

        const {task_expected_date, ...rest} = user as typeof user & {
          task_expected_date?: string | null
        }
        return {...rest, expected_date: normalizedValue}
      })

      setData(updated)
      formikSetFieldValue('sequence_responsibles', updated)
      formikSetFieldValue('users', updated)
      if (onAutoSave) {
        onAutoSave({sequence_responsibles: updated})
      }
      
      delete originalDateValuesRef.current[index]
      isSelectingDateRef.current = false
      return
    }
    
    if (!isSelectingDateRef.current) {
      const input = dateInputsRef.current[index]
      if (input && originalValue !== undefined) {
        input.value = originalValue || ''
      }
      return
    }
  }

  useEffect(() => {
    if (activeDateIndex !== null) {
      const input = dateInputsRef.current[activeDateIndex]
      if (input) {
        const originalValue = data[activeDateIndex]?.expected_date || null
        originalDateValuesRef.current[activeDateIndex] = originalValue
        
        input.focus()
        try {
          // @ts-ignore - showPicker pode não estar disponível em todos os navegadores
          if (typeof (input as any).showPicker === 'function') {
            (input as any).showPicker()
          }
        } catch (error) {
        }

        let blurTimeout: NodeJS.Timeout | null = null
        let changeTimeout: NodeJS.Timeout | null = null

        const handleChange = (e: Event) => {
          const target = e.target as HTMLInputElement
          const newValue = target.value || null
          const originalValue = originalDateValuesRef.current[activeDateIndex]
          
          if (changeTimeout) {
            clearTimeout(changeTimeout)
          }
          
          if (newValue && newValue !== originalValue) {
            handleDateChange(activeDateIndex, newValue, true)
            setTimeout(() => {
              setActiveDateIndex(null)
            }, 150)
          }
        }

        const handleBlur = (e: FocusEvent) => {
          if (blurTimeout) {
            clearTimeout(blurTimeout)
          }

          blurTimeout = setTimeout(() => {
            const activeElement = document.activeElement
            
            if (activeElement === input) {
              return
            }
            
            if (activeElement?.closest('.date-picker-wrapper')) {
              return
            }
            
            const currentValue = input.value || null
            const originalValue = originalDateValuesRef.current[activeDateIndex]
            
            if (currentValue && currentValue !== originalValue) {
              handleDateChange(activeDateIndex, currentValue, true)
            } else if (currentValue === originalValue) {
              if (input && originalValue !== undefined) {
                input.value = originalValue || ''
              }
            }
            
            setActiveDateIndex(null)
          }, 300)
        }

        const handleFocus = () => {
          if (blurTimeout) {
            clearTimeout(blurTimeout)
            blurTimeout = null
          }
        }

        const handleClickOutside = (e: MouseEvent) => {
          const target = e.target as HTMLElement
          if (!target.closest('.date-picker-wrapper') && 
              target !== input &&
              !target.closest('input[type="date"]')) {
            
            const currentValue = input.value || null
            const originalValue = originalDateValuesRef.current[activeDateIndex]
            
            if (currentValue && currentValue !== originalValue) {
              handleDateChange(activeDateIndex, currentValue, true)
            }
            
            setActiveDateIndex(null)
          }
        }

        input.addEventListener('change', handleChange)
        input.addEventListener('blur', handleBlur, true)
        input.addEventListener('focus', handleFocus, true)
        document.addEventListener('mousedown', handleClickOutside, true)
        
        return () => {
          if (blurTimeout) {
            clearTimeout(blurTimeout)
          }
          if (changeTimeout) {
            clearTimeout(changeTimeout)
          }
          input.removeEventListener('change', handleChange)
          input.removeEventListener('blur', handleBlur, true)
          input.removeEventListener('focus', handleFocus, true)
          document.removeEventListener('mousedown', handleClickOutside, true)
        }
      }
    } else {
      isSelectingDateRef.current = false
    }
  }, [activeDateIndex, data])

  const hasSequenceDates = data.some(
    (responsible) => responsible?.expected_date && responsible.expected_date !== '' && responsible.expected_date !== 'null'
  )

  const content = (
    <div className='pt-6'>
      {hasSequenceDates && (
        <div className='d-flex align-items-start mb-4 p-3 bg-gray-100 rounded border border-gray-300'>
          <i className='bi bi-info-circle me-3 fs-5 text-muted'></i>
          <div className='text-start'>
            <div className='fw-semibold text-dark mb-1'>Atenção</div>
            <div className='fs-7 text-muted'>
              Esta tarefa possui datas definidas na sequência de responsáveis. A data de vencimento da tarefa não será considerada quando houver datas individuais para os responsáveis.
            </div>
          </div>
        </div>
      )}
      
      <div className='timeline'>
        {users?.length > 0 ? (
          <SequenceResponsiblesSymbolGroup
            classes={`justify-content-start mb-4 ${data?.length > 0 ? '' : 'ms-7'}`}
            users={data}
            //@ts-ignore
            usersAll={users}
            onChange={(users: any) => {
              try {
                const lastUser = data[data.length - 1]
                const lastUserId = lastUser?.user_id ?? null
                const newUserId = users?.user_id ?? null

                // Só adiciona se o novo usuário for diferente do último
                if (lastUserId !== newUserId) {
                  const {task_expected_date, ...rest} = users as typeof users & {
                    task_expected_date?: string | null
                  }
                  const newData = [
                    ...data,
                    {
                      ...rest,
                      task_concluded: 0,
                      expected_date: users?.expected_date ?? task_expected_date ?? null,
                    },
                  ]
                  setData(newData)
                  formikSetFieldValue('sequence_responsibles', newData)
                  formikSetFieldValue('users', newData)
                  if (onAutoSave) {
                    onAutoSave({sequence_responsibles: newData})
                  }
                }
              } catch (error) {
                console.error('Error adding user to sequence:', error)
              }
            }}
            placement='bottom-start'
          />
        ) : null}

        <div className='checklist pt-6'>
          <div className='checklist-items'>
            {Array.isArray(data) &&
              data.map((user, index) => (
                <div
                  key={user.user_id + '-' + index}
                  className={`check d-flex align-items-center ${
                    draggedIndex === index ? 'dragging' : ''
                  } ${dragOverIndex === index ? 'drag-over' : ''}`}
                  onDragOver={handleDragOverWrapper(index)}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <div
                    className='drag-handle me-3'
                    role='button'
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    title='Arrastar para reordenar'
                  >
                    <i className='bi bi-grip-vertical text-gray-500'></i>
                  </div>

                  <div className='timeline-icon me-4'>
                    <UserAvatar
                      name={user?.name ?? ''}
                      avatar={user?.avatar}
                      sizeClass='symbol-40px'
                      imgClassName='object-fit-cover'
                    />
                  </div>
                  <div className='overflow-auto pe-3 flex-grow-1'>
                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='fs-5 fw-semibold'>{user.name}</span>

                    <OverlayTrigger
                      placement='top'
                      overlay={
                        <Tooltip>
                          {user.expected_date ? 'Editar data prevista' : 'Definir data prevista'}
                        </Tooltip>
                      }
                    >
                      <button
                        type='button'
                        className='btn p-0'
                        style={{lineHeight: 1}}
                        onClick={() => toggleDatePicker(index)}
                      >
                        <i
                          className={`bi bi-calendar-event fs-5 ${
                            user.expected_date ? 'text-primary' : 'text-muted'
                          }`}
                        ></i>
                      </button>
                    </OverlayTrigger>

                    {user.expected_date && (
                      <span className='badge bg-light-primary text-primary'>
                        {formatTaskDateTimeDisplay(user.expected_date)}
                      </span>
                    )}

                      {activeDateIndex === index && (
                      <div className='d-flex align-items-center gap-2 date-picker-wrapper'>
                        <input
                          type='date'
                          className='form-control form-control-sm'
                          style={{minWidth: '170px'}}
                          defaultValue={parseTaskDateTime(user.expected_date ?? '').date ?? ''}
                          min={(() => {
                            const today = dayjs().startOf('day')
                            const maxPreviousDate = getMaxPreviousDate(data, index)

                            if (maxPreviousDate && maxPreviousDate.isAfter(today)) {
                              return maxPreviousDate.format('YYYY-MM-DD')
                            }

                            return today.format('YYYY-MM-DD')
                          })()}
                          onMouseDown={(e) => {
                            e.stopPropagation()
                          }}
                          onClick={(e) => {
                            e.stopPropagation()
                          }}
                          ref={(element) => {
                            if (element) {
                              dateInputsRef.current[index] = element
                              const originalValue = originalDateValuesRef.current[index]
                              if (originalValue !== undefined && element.value !== originalValue) {
                                element.value = originalValue || ''
                              }
                            } else {
                              delete dateInputsRef.current[index]
                            }
                          }}
                        />
                          <button
                            type='button'
                            className='btn btn-sm btn-icon btn-light-danger'
                            onClick={() => {
                              handleDateChange(index, '', true)
                              setActiveDateIndex(null)
                            }}
                            title='Limpar data'
                            disabled={!user.expected_date}
                          >
                            <i className='bi bi-x-circle'></i>
                          </button>
                      </div>
                    )}
                  </div>
                </div>
                  <div className='ms-auto'>
                    {/* @ts-ignore */}
                    {user.task_concluded && !isClone ? (
                      <div className='btn p-0 ms-5'>
                        <i className='bi bi-check-square-fill text-success'></i>
                      </div>
                    ) : (
                      <button
                        //@ts-ignore
                        onClick={() => removeResponsible(index)}
                        className='btn p-0 ms-5'
                      >
                        <i className='bi bi-x-square-fill'></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  )

  return tabId ? (
    <div id={tabId} className='tab-pane fade'>
      {content}
    </div>
  ) : (
    content
  )
}
